import React, { useMemo } from "react";

import moment from "moment";
import { renderToStaticMarkup } from "react-dom/server";

import ContentTooltipItem from "./ContentTooltipItem";
import useFAST from "../../../contexts/FASTContext";
import {
  getItemRendererStyle,
  parseCssTextValue,
  showItemTitle,
} from "../../../utils/fast";

import type {
  ItemRendererGetItemPropsReturnType,
  ReactCalendarItemRendererProps,
} from "okast-timeline";
import type { CSSProperties, JSX } from "react";

import type { SchedulingTimeslotItem } from "./timeline.types";

interface SchedulingTimeslotItemRendererProps {
  isVerticalTimeline: boolean;
  item: SchedulingTimeslotItem;
  itemContext: ReactCalendarItemRendererProps<SchedulingTimeslotItem>["itemContext"];
  itemProps: ItemRendererGetItemPropsReturnType<SchedulingTimeslotItem>;
}

function SchedulingTimeslotItemRenderer({
  itemProps,
  item,
  itemContext,
  isVerticalTimeline,
}: SchedulingTimeslotItemRendererProps): JSX.Element {
  const { ref, style } = itemProps;
  const { dispatch } = useFAST();

  const { height: itemPropsHeight = 0, width: itemPropsWidth = 0 } = style;
  const isSelected = itemContext.selected;
  const { truncated } = itemContext.dimensions;

  const formatedItemPropsHeight =
    typeof itemPropsHeight === "string"
      ? parseCssTextValue(itemPropsHeight)
      : itemPropsHeight;

  const formatedItemPropsWidth =
    typeof itemPropsWidth === "string"
      ? parseCssTextValue(itemPropsWidth)
      : itemPropsWidth;

  const displayTitle = showItemTitle({
    isVertical: isVerticalTimeline,
    size: isVerticalTimeline ? formatedItemPropsHeight : formatedItemPropsWidth,
  });

  const styleItemCustom = getItemRendererStyle({
    color: "black",
    isSelected,
    isVerticalTimeline,
    truncated,
  });

  const templateBorderRadius = useMemo((): string => {
    if (truncated === "start-end") {
      return "0 0 0 0";
    }
    if (truncated === "start") {
      return "0 6px 0 0";
    }
    if (truncated === "end") {
      return "6px 0 0 0";
    }
    return "6px 6px 0 0";
  }, [truncated]);

  const templateItemStyle = useMemo<CSSProperties>(
    () => ({
      ...styleItemCustom,
      height: 27,
      position: "relative",
      cursor: "pointer",
      borderRight: "none",
      borderLeft: "none",
      borderRadius: templateBorderRadius,
    }),
    [styleItemCustom, templateBorderRadius],
  );

  const templateDuration = useMemo<number>(
    () => moment(item.end_time).diff(moment(item.start_time), "milliseconds"),
    [item.end_time, item.start_time],
  );

  const handleClickItem = (): void => {
    dispatch({
      type: "SET_ITEM_SELECTED",
      id: item.id,
    });
  };

  return (
    <div
      ref={ref}
      data-tooltip-id="timeline-tooltip"
      data-tooltip-place="bottom"
      onClick={handleClickItem}
      style={templateItemStyle}
      data-tooltip-html={renderToStaticMarkup(
        <ContentTooltipItem
          duration={templateDuration}
          endTime={item.end_time}
          startTime={item.start_time}
          title={itemContext.title}
        />,
      )}
    >
      <div className="pointer-events-none flex size-full items-center justify-center">
        {displayTitle ? (
          <React.Fragment>
            <div
              className="mx-[0.3125rem] size-[0.625rem] shrink-0 rounded-full"
              style={{ backgroundColor: item.timeslot.color }}
            />
            <span className="truncate p-[0.15625rem]">{itemContext.title}</span>
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
}

export default React.memo(SchedulingTimeslotItemRenderer);
